import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, MessageSquare, Search, ShieldCheck, ShoppingCart, Store } from "lucide-react";
import { ButtonLink, Logo, Skeleton } from "@/components/ui";
import { MegaMenu } from "@/components/search/MegaMenu";
import { useCategoryTree } from "@/features/catalog/api";
import { useCartCount } from "@/features/cart/api";
import { useChatsUnread } from "@/features/chats/api";
import { LocationChip } from "@/features/locations/LocationChip";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { useInstall } from "@/features/pwa";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/cn";
import { useAuth } from "@/stores/auth";
import { useUI } from "@/stores/ui";
import { UserMenu } from "./UserMenu";

/** The quick category row under the header (desktop). Names map to real category slugs. */
export const NAV_CATEGORIES: { label: string; slug: string }[] = [
  { label: "Phones & Tablets", slug: "phones-tablets" },
  { label: "Fashion", slug: "fashion" },
  { label: "Power & Solar", slug: "power-energy" },
  { label: "Cars", slug: "vehicles" },
  { label: "Property", slug: "property" },
  { label: "Jobs", slug: "jobs" },
  { label: "Services", slug: "services" },
  { label: "Electronics", slug: "electronics" },
  { label: "Home & Furniture", slug: "home-furniture" },
  { label: "Food & Agric", slug: "agriculture-food" },
];

/** 32px utility links above the header. */
export function UtilityBar() {
  const status = useAuth((s) => s.status);
  const isStaff = useAuth((s) => !!s.user?.is_staff);
  const install = useInstall();
  const link = "text-ink-soft hover:text-ink hover:underline";
  return (
    <div className="border-b border-line bg-surface2/70">
      <div className="container-rs flex h-8 items-center gap-5 text-xs">
        <Link to="/sell" className={link} onClick={() => track("home_sell_tap", { source: "utility_bar" })}>
          Sell on RealityStore
        </Link>
        <Link to="/safety" className={link}>
          How RealitySafe works
        </Link>
        <Link to="/help" className={link}>
          Help
        </Link>
        {install.available && (
          <button type="button" onClick={() => void install.install()} className={link}>
            Download app
          </button>
        )}
        <span className="flex-1" />
        {status === "authenticated" && isStaff && (
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-tint)] px-2.5 py-0.5 font-semibold text-[var(--primary-text)] hover:underline"
          >
            <ShieldCheck className="size-3.5" aria-hidden /> Admin console
          </Link>
        )}
        {status !== "authenticated" && (
          <>
            <Link to="/login" className={cn(link, "font-medium text-ink")}>
              Sign in
            </Link>
            <Link to="/register" className={link}>
              Create account
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/** Big search with a category dropdown (≈50% of the header). Submits to /search. */
function HeaderSearch() {
  const navigate = useNavigate();
  const tree = useCategoryTree();
  const setSearchOpen = useUI((s) => s.setSearchOpen);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    if (!term && !category) return setSearchOpen(true);
    track("home_search_used", { source: "header", ...(category ? { slug: category } : {}) });
    const params = new URLSearchParams();
    if (term) params.set("q", term);
    navigate(category ? `/c/${category}${term ? `?${params}` : ""}` : `/search?${params}`);
  };
  return (
    <form
      role="search"
      onSubmit={submit}
      className="flex h-12 min-w-0 flex-1 items-stretch overflow-hidden rounded-full border border-line bg-surface shadow-card focus-within:border-[var(--primary)]"
    >
      <label className="relative hidden shrink-0 items-center border-r border-line bg-surface2 xl:flex">
        <span className="sr-only">Category</span>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-full max-w-[13rem] cursor-pointer appearance-none bg-surface2 pl-4 pr-8 text-sm font-medium text-ink outline-none [&>option]:bg-elevated [&>option]:text-ink"
        >
          <option value="">All categories</option>
          {(tree.data ?? []).map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 size-4 text-ink-muted" aria-hidden />
      </label>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search RealityStore"
        placeholder="Search phones, cars, homes, jobs, solar…"
        className="min-w-0 flex-1 bg-transparent px-4 text-[15px] text-ink outline-none placeholder:text-ink-muted"
      />
      <button
        type="submit"
        className="m-1 inline-flex items-center gap-2 rounded-full bg-[var(--primary-strong)] px-5 text-sm font-semibold text-white hover:bg-primary-hover"
        aria-label="Search"
      >
        <Search className="size-4" aria-hidden /> <span className="hidden 2xl:inline">Search</span>
      </button>
    </form>
  );
}

function ChatsLink() {
  const unread = useChatsUnread().data?.total ?? 0;
  return (
    <Link
      to="/chats"
      className="relative grid size-10 place-items-center rounded-full text-ink-soft transition hover:bg-fg/5 hover:text-ink"
      aria-label={unread ? `Chats, ${unread} unread` : "Chats"}
    >
      <MessageSquare className="size-5" aria-hidden />
      {unread > 0 && (
        <span
          className="tabular absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[var(--magenta-strong)] px-1 text-[11px] font-semibold text-white"
          aria-hidden
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}

/** Desktop header (72px): logo · location · big search · Sell for free · chats · alerts · cart · account. */
export function DesktopMainBar() {
  const status = useAuth((s) => s.status);
  const isVendor = useAuth((s) => !!s.user?.is_vendor);
  const setCartOpen = useUI((s) => s.setCartOpen);
  const cartCount = useCartCount();
  return (
    <div className="container-rs flex h-[72px] items-center gap-4">
      <Link to="/" aria-label="RealityStore home" className="shrink-0 rounded-lg">
        <Logo className="[&_img]:h-9" />
      </Link>
      <LocationChip />
      <div className="mx-2 flex min-w-0 flex-1 justify-center">
        <div className="flex w-full max-w-[760px]">
          <HeaderSearch />
        </div>
      </div>
      <ButtonLink
        to={isVendor ? "/vendor" : "/sell"}
        size="md"
        className="shrink-0"
        onClick={() => track("home_sell_tap", { source: "header" })}
      >
        <Store className="size-4" aria-hidden /> {isVendor ? "My store" : "Sell for free"}
      </ButtonLink>
      <div className="flex shrink-0 items-center gap-1">
        {status === "authenticated" && (
          <>
            <ChatsLink />
            <NotificationBell />
          </>
        )}
        <button
          type="button"
          data-cart-target
          onClick={() => setCartOpen(true)}
          className="relative grid size-10 place-items-center rounded-full text-ink-soft transition hover:bg-fg/5 hover:text-ink"
          aria-label={cartCount ? `Cart, ${cartCount} item${cartCount === 1 ? "" : "s"}` : "Cart"}
        >
          <ShoppingCart className="size-5" aria-hidden />
          {cartCount > 0 && (
            <span
              className="tabular absolute right-0 top-0 grid h-5 min-w-5 place-items-center rounded-full bg-[var(--primary-strong)] px-1 text-[11px] font-semibold text-white"
              aria-hidden
            >
              {cartCount > 99 ? "99+" : cartCount}
            </span>
          )}
        </button>
        {status === "loading" || status === "idle" ? (
          <Skeleton className="h-9 w-24 rounded-full" />
        ) : status === "authenticated" ? (
          <UserMenu />
        ) : (
          <ButtonLink to="/login" variant="ghost" size="sm">
            Sign in
          </ButtonLink>
        )}
      </div>
    </div>
  );
}

/** Category row under the header; "More" opens the full mega-menu. */
export function CategoryNavRow() {
  const [path, setPath] = useState(typeof window !== "undefined" ? window.location.pathname : "/");
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return (
    <nav aria-label="Categories" className="border-t border-line">
      <div className="container-rs flex h-11 items-center gap-1">
        {NAV_CATEGORIES.map((c, i) => (
          <Link
            key={c.slug}
            to={`/c/${c.slug}`}
            aria-current={path === `/c/${c.slug}` ? "page" : undefined}
            onClick={() => {
              setPath(`/c/${c.slug}`);
              track("home_category_tap", { slug: c.slug, position: i + 1, source: "nav_row" });
            }}
            className={cn(
              i >= 7 && "hidden xl:inline-block",
              "shrink-0 rounded-full px-3 py-1.5 text-sm font-medium text-ink-soft transition hover:bg-fg/5 hover:text-ink aria-[current=page]:bg-[var(--brand-tint)] aria-[current=page]:text-[var(--primary-text)]",
            )}
          >
            {c.label}
          </Link>
        ))}
        <MegaMenu label="More" align="right" />
      </div>
    </nav>
  );
}
